import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, Clock, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';

const ENTITY_LABEL = { expense: 'Dépense', document: 'Document', project_task: 'Tâche', budget_line: 'Ligne budgétaire' };

export default function Approvals() {
    const { t } = useLang();
    const { user, loading: authLoading } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supported, setSupported] = useState(true);

    const load = async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('workflow_approvals')
            .select('*')
            .eq('workspace_id', currentWorkspace.id)
            .order('created_at', { ascending: false }).limit(200);
        if (error && error.code === 'PGRST205') setSupported(false);
        setRows(data || []);
        setLoading(false);
    };
    useEffect(() => { if (!authLoading && user) load(); /* eslint-disable-next-line */ }, [authLoading, user, currentWorkspace]);

    const decide = async (id, status) => {
        await supabase.from('workflow_approvals').update({ status, approver_id: user.id, decided_at: new Date().toISOString() }).eq('id', id);
        load();
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">{t('common.no_workspace')}</div>;
    if (!supported) return (
        <div className="card-soft p-10 text-center">
            <ShieldCheck size={28} className="mx-auto text-primary mb-3" />
            <p className="text-foreground/70">Le module Workflow nécessite l'exécution de <code className="text-xs bg-surface-muted px-2 py-0.5 rounded">/app/supabase/schema_v2.sql</code>.</p>
        </div>
    );

    const pending = rows.filter(r => r.status === 'pending');
    const recent = rows.filter(r => r.status !== 'pending').slice(0, 50);

    return (
        <div className="space-y-8">
            <div>
                <div className="eyebrow mb-2">Validation multi-niveaux</div>
                <h1 className="font-heading text-4xl">{t('approvals.title')}</h1>
                <p className="text-foreground/70 mt-3">{t('approvals.sub')}</p>
            </div>

            <div className="card-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <h2 className="font-heading text-2xl">En attente ({pending.length})</h2>
                </div>
                {loading && <div className="p-10 text-center text-muted-foreground"><Loader2 size={14} className="inline animate-spin mr-2" /> {t('common.loading')}</div>}
                {!loading && pending.length === 0 && <div className="p-10 text-center text-foreground/60" data-testid="approvals-empty">{t('approvals.empty')}</div>}
                {!loading && pending.length > 0 && (
                    <table className="w-full">
                        <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr><th className="px-6 py-3">Objet</th><th className="px-6 py-3">Niveau</th><th className="px-6 py-3">Demandé le</th><th className="px-6 py-3 text-right">Actions</th></tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {pending.map(r => (
                                <tr key={r.id} data-testid={`approval-row-${r.id}`}>
                                    <td className="px-6 py-3">{ENTITY_LABEL[r.entity_type] || r.entity_type} <span className="text-xs text-muted-foreground font-mono">{r.entity_id?.slice(0, 8)}</span></td>
                                    <td className="px-6 py-3"><span className="phase-badge bg-primary-light text-primary inline-flex items-center"><Clock size={10} className="mr-1" /> {r.level_label}</span></td>
                                    <td className="px-6 py-3 text-sm">{new Date(r.created_at).toLocaleString()}</td>
                                    <td className="px-6 py-3 text-right">
                                        <Button size="sm" variant="ghost" onClick={() => decide(r.id, 'approved')} className="text-secondary hover:bg-secondary-light" data-testid={`approval-approve-${r.id}`}><CheckCircle2 size={14} className="mr-1" /> Approuver</Button>
                                        <Button size="sm" variant="ghost" onClick={() => decide(r.id, 'rejected')} className="text-destructive hover:bg-destructive/10 ml-2" data-testid={`approval-reject-${r.id}`}><XCircle size={14} className="mr-1" /> Rejeter</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {recent.length > 0 && (
                <div className="card-soft overflow-hidden">
                    <div className="px-6 py-4 border-b border-border"><h2 className="font-heading text-2xl">Historique récent</h2></div>
                    <table className="w-full">
                        <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr><th className="px-6 py-3">Objet</th><th className="px-6 py-3">Niveau</th><th className="px-6 py-3">Décision</th><th className="px-6 py-3">Décidé le</th></tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {recent.map(r => (
                                <tr key={r.id}>
                                    <td className="px-6 py-3">{ENTITY_LABEL[r.entity_type] || r.entity_type}</td>
                                    <td className="px-6 py-3">{r.level_label}</td>
                                    <td className="px-6 py-3"><span className={`phase-badge ${r.status === 'approved' ? 'bg-secondary-light text-secondary' : 'bg-destructive/10 text-destructive'} inline-flex items-center`}>{r.status === 'approved' ? <CheckCircle2 size={10} className="mr-1" /> : <XCircle size={10} className="mr-1" />} {r.status}</span></td>
                                    <td className="px-6 py-3 text-sm">{r.decided_at ? new Date(r.decided_at).toLocaleString() : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
